import { z } from 'zod';

// Password validation schema
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters long')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[^a-zA-Z0-9]/, 'Password must contain at least one special character');

// NIN validation (exactly 11 digits for Nigerian format)
export const ninSchema = z
  .string()
  .regex(/^\d{11}$/, 'NIN must be exactly 11 digits')
  .refine((val) => val.length === 11, 'NIN must be exactly 11 digits');

// Phone validation (10-15 digits, with optional country code)
export const phoneSchema = z
  .string()
  .regex(/^[\+]?[0-9\s\-\(\)]{10,15}$/, 'Please enter a valid phone number')
  .transform((val) => val.replace(/\s/g, '')); // Remove spaces

// Age validation (must be 18+)
const eighteenYearsAgo = new Date();
eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);

export const dateOfBirthSchema = z
  .string()
  .transform((str) => new Date(str))
  .refine((date) => date <= eighteenYearsAgo, 'You must be at least 18 years old')
  .refine((date) => !isNaN(date.getTime()), 'Please enter a valid date');

// Nigerian states for validation
const nigerianStates = [
  'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue',
  'Borno', 'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu',
  'Federal Capital Territory', 'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano',
  'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger',
  'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto',
  'Taraba', 'Yobe', 'Zamfara'
];

// Registration form schema
export const registrationSchema = z.object({
  // Personal Information
  first_name: z
    .string()
    .min(2, 'First name must be at least 2 characters')
    .max(80, 'First name must be less than 80 characters')
    .regex(/^[a-zA-Z\s'-]+$/, 'First name can only contain letters, spaces, hyphens, and apostrophes'),
  
  middle_name: z
    .string()
    .max(80, 'Middle name must be less than 80 characters')
    .regex(/^[a-zA-Z\s'-]*$/, 'Middle name can only contain letters, spaces, hyphens, and apostrophes')
    .optional()
    .or(z.literal('')),
  
  last_name: z
    .string()
    .min(2, 'Last name must be at least 2 characters')
    .max(80, 'Last name must be less than 80 characters')
    .regex(/^[a-zA-Z\s'-]+$/, 'Last name can only contain letters, spaces, hyphens, and apostrophes'),
  
  // Identity Information
  nin: ninSchema,
  date_of_birth: z.string().refine((date) => {
    const birthDate = new Date(date);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return age - 1 >= 18;
    }
    return age >= 18;
  }, 'You must be at least 18 years old'),
  
  nationality: z.string().default('Nigerian'),
  
  // Contact Information
  email: z.string().email('Please enter a valid email address'),
  phone: phoneSchema,
  
  // Address Information
  address_line1: z
    .string()
    .min(5, 'Address must be at least 5 characters')
    .max(160, 'Address must be less than 160 characters'),
  
  address_line2: z
    .string()
    .max(160, 'Address must be less than 160 characters')
    .optional()
    .or(z.literal('')),
  
  city: z
    .string()
    .min(2, 'City must be at least 2 characters')
    .max(80, 'City must be less than 80 characters'),
  
  state: z
    .string()
    .min(1, 'Please select a state')
    .refine((val) => nigerianStates.includes(val), 'Please select a valid Nigerian state'),
  
  postal_code: z
    .string()
    .regex(/^\d{6}$/, 'Postal code must be 6 digits')
    .optional()
    .or(z.literal('')),
  
  country: z.string().default('Nigeria'),
  
  // Employment (Optional)
  employer: z
    .string()
    .max(160, 'Employer name must be less than 160 characters')
    .optional()
    .or(z.literal('')),
  
  // Authentication
  password: passwordSchema,
  confirm_password: z.string(),
  
  // Legal
  accept_terms: z.boolean().refine((val) => val === true, 'You must accept the terms and conditions'),
}).refine((data) => data.password === data.confirm_password, {
  message: 'Passwords do not match',
  path: ['confirm_password'],
});

// Export type for TypeScript
export type RegistrationFormData = z.infer<typeof registrationSchema>;

// Validation helpers
export const validateField = (field: keyof RegistrationFormData, value: any) => {
  try {
    const fieldSchema = registrationSchema.shape[field];
    if (fieldSchema) {
      fieldSchema.parse(value);
      return null;
    }
    return null;
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.errors[0]?.message || 'Invalid input';
    }
    return 'Invalid input';
  }
};

// Nigerian states export for components
export { nigerianStates };

// Password strength checker
export const getPasswordStrength = (password: string): {
  score: number;
  label: string;
  color: string;
} => {
  let score = 0;
  
  if (password.length >= 8) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;
  
  const strengthMap = {
    0: { label: 'Very Weak', color: 'bg-red-500' },
    1: { label: 'Very Weak', color: 'bg-red-500' },
    2: { label: 'Weak', color: 'bg-orange-500' },
    3: { label: 'Fair', color: 'bg-yellow-500' },
    4: { label: 'Good', color: 'bg-blue-500' },
    5: { label: 'Strong', color: 'bg-green-500' },
  };
  
  return {
    score,
    label: strengthMap[score as keyof typeof strengthMap].label,
    color: strengthMap[score as keyof typeof strengthMap].color,
  };
};